import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { downloadLoadtestBundle, useLoadtestScenarios, useLoadtestSystems, useLoadtestWarmupAvailable, type LoadtestRequest } from "@/lib/loadtest";
import { Field, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";

/** Aktuální origin jako výchozí cíl. */
function originDefaults() {
  if (typeof window === "undefined") return { protocol: "http", host: "localhost", port: "6280" };
  return {
    protocol: window.location.protocol.replace(":", "") === "https" ? "https" : "http",
    host: window.location.hostname || "localhost",
    port: window.location.port || "6280",
  };
}

export function LoadtestGeneratorPage() {
  const { t } = useTranslation();
  const systems = useLoadtestSystems();
  const scenarios = useLoadtestScenarios();
  const warmup = useLoadtestWarmupAvailable();
  const od = originDefaults();

  const [source, setSource] = useState<"LIST" | "PATTERN">("LIST");
  const [f, setF] = useState({
    targetSystem: "",
    scenario: "",
    warmupMode: "RUNTIME",
    // LIST
    identitiesCsv: "",
    // PATTERN
    usernamePrefix: "7####",
    startIndex: "0",
    endIndex: "99",
    padWidth: "0",
    phonePattern: "+420724######",
    patternPassword: "PIN123",
    patternPaat: "",
    // load
    threads: "10",
    rampUpSeconds: "30",
    loops: "1",
    // target
    protocol: od.protocol,
    host: od.host,
    port: od.port,
    authServerType: "ECOBRA",
    // warmup
    clientIdent: "",
    systemId: "",
    deviceOSType: "Android",
    reason: "eCobraToken",
    sendSms: true,
    setPhone: true,
    // api key
    embedApiKey: false,
    apiKey: "",
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const num = (s: string) => (s.trim() === "" ? 0 : parseInt(s, 10));
  const numOrNull = (s: string) => (s.trim() === "" ? null : parseInt(s, 10));
  const strOrNull = (s: string) => (s.trim() === "" ? null : s.trim());

  const generate = async () => {
    setMsg(null);
    const req: LoadtestRequest = {
      targetSystem: f.targetSystem || systems.data?.[0]?.id || "",
      scenario: f.scenario || scenarios.data?.[0]?.id || "",
      warmupMode: f.warmupMode,
      identitySource:
        source === "LIST"
          ? { type: "LIST", csv: f.identitiesCsv, pattern: null }
          : {
              type: "PATTERN",
              csv: null,
              pattern: {
                usernamePrefix: f.usernamePrefix,
                startIndex: num(f.startIndex),
                endIndex: num(f.endIndex),
                padWidth: num(f.padWidth),
                phonePattern: strOrNull(f.phonePattern),
                password: strOrNull(f.patternPassword),
                paatProfileId: strOrNull(f.patternPaat),
              },
            },
      load: { threads: num(f.threads), rampUpSeconds: num(f.rampUpSeconds), loops: num(f.loops) },
      target: { protocol: f.protocol, host: f.host, port: num(f.port), authServerType: f.authServerType },
      warmup: {
        clientIdent: strOrNull(f.clientIdent),
        system: numOrNull(f.systemId),
        deviceOSType: f.deviceOSType,
        reason: strOrNull(f.reason),
        sendSms: f.sendSms,
        setPhone: f.setPhone,
      },
      embedApiKey: f.embedApiKey,
      apiKey: strOrNull(f.apiKey),
    };

    setBusy(true);
    try {
      const res = await downloadLoadtestBundle(req);
      setMsg(res.ok ? { ok: true, text: t("loadtest.generated") } : { ok: false, text: res.error ?? t("common.loadError") });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("loadtest.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("loadtest.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {/* Dostupnost warm-up provideru v tomto buildu */}
      {warmup.data != null && (
        warmup.data.length === 0 ? (
          <div className="rounded-lg bg-primary px-4 py-3 text-sm text-warning-primary ring-1 ring-warning">
            {t("loadtest.warmup.unavailable")}
          </div>
        ) : (
          <div className="rounded-lg bg-primary px-4 py-3 text-sm text-tertiary ring-1 ring-secondary">
            {t("loadtest.warmup.available", { types: warmup.data.join(", ") })}
          </div>
        )
      )}

      {/* Scénář */}
      <Section title={t("loadtest.section.scenario")}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t("loadtest.targetSystem")}>
            {systems.isLoading ? (
              <LoadingIndicator />
            ) : (
              <AppSelect value={f.targetSystem} onChange={(e) => set("targetSystem", e.target.value)}>
                {(systems.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName}
                  </option>
                ))}
              </AppSelect>
            )}
          </Field>
          <Field label={t("loadtest.scenario")}>
            <AppSelect value={f.scenario} onChange={(e) => set("scenario", e.target.value)}>
              {(scenarios.data ?? []).map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.displayName}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label={t("loadtest.warmupMode")}>
            <AppSelect value={f.warmupMode} onChange={(e) => set("warmupMode", e.target.value)}>
              <option value="RUNTIME">{t("loadtest.warmup.runtime")}</option>
              <option value="PREGENERATED">{t("loadtest.warmup.pregenerated")}</option>
            </AppSelect>
          </Field>
        </div>
      </Section>

      {/* Identity */}
      <Section title={t("loadtest.section.identities")}>
        <div className="flex gap-1">
          {(["LIST", "PATTERN"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                source === s ? "bg-brand-solid text-white" : "text-secondary hover:bg-primary_hover"
              }`}
            >
              {t(s === "LIST" ? "loadtest.source.list" : "loadtest.source.pattern")}
            </button>
          ))}
        </div>
        {source === "LIST" ? (
          <Field label={t("loadtest.identitiesCsv")} hint={t("loadtest.identitiesCsv.hint")}>
            <textarea className={`${inputCls} h-28 font-mono`} value={f.identitiesCsv} onChange={(e) => set("identitiesCsv", e.target.value)} />
          </Field>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("loadtest.usernamePrefix")}>
              <input className={inputCls} value={f.usernamePrefix} onChange={(e) => set("usernamePrefix", e.target.value)} />
            </Field>
            <Field label={t("loadtest.startIndex")}>
              <input className={inputCls} type="number" value={f.startIndex} onChange={(e) => set("startIndex", e.target.value)} />
            </Field>
            <Field label={t("loadtest.endIndex")}>
              <input className={inputCls} type="number" value={f.endIndex} onChange={(e) => set("endIndex", e.target.value)} />
            </Field>
            <Field label={t("loadtest.padWidth")}>
              <input className={inputCls} type="number" value={f.padWidth} onChange={(e) => set("padWidth", e.target.value)} />
            </Field>
            <Field label={t("loadtest.phonePattern")}>
              <input className={inputCls} value={f.phonePattern} onChange={(e) => set("phonePattern", e.target.value)} />
            </Field>
            <Field label={t("loadtest.password")}>
              <input className={inputCls} value={f.patternPassword} onChange={(e) => set("patternPassword", e.target.value)} />
            </Field>
            <Field label={t("loadtest.paatProfile")}>
              <input className={inputCls} value={f.patternPaat} onChange={(e) => set("patternPaat", e.target.value)} />
            </Field>
          </div>
        )}
      </Section>

      {/* Load + Target */}
      <Section title={t("loadtest.section.load")}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t("loadtest.threads")}>
            <input className={inputCls} type="number" value={f.threads} onChange={(e) => set("threads", e.target.value)} />
          </Field>
          <Field label={t("loadtest.rampUp")}>
            <input className={inputCls} type="number" value={f.rampUpSeconds} onChange={(e) => set("rampUpSeconds", e.target.value)} />
          </Field>
          <Field label={t("loadtest.loops")}>
            <input className={inputCls} type="number" value={f.loops} onChange={(e) => set("loops", e.target.value)} />
          </Field>
          <Field label={t("loadtest.protocol")}>
            <AppSelect value={f.protocol} onChange={(e) => set("protocol", e.target.value)}>
              <option value="http">http</option>
              <option value="https">https</option>
            </AppSelect>
          </Field>
          <Field label={t("loadtest.host")}>
            <input className={inputCls} value={f.host} onChange={(e) => set("host", e.target.value)} />
          </Field>
          <Field label={t("loadtest.port")}>
            <input className={inputCls} type="number" value={f.port} onChange={(e) => set("port", e.target.value)} />
          </Field>
        </div>
      </Section>

      {/* Warmup */}
      <Section title={t("loadtest.section.warmup")}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t("loadtest.clientIdent")}>
            <input className={inputCls} value={f.clientIdent} onChange={(e) => set("clientIdent", e.target.value)} />
          </Field>
          <Field label={t("loadtest.systemId")}>
            <input className={inputCls} type="number" value={f.systemId} onChange={(e) => set("systemId", e.target.value)} />
          </Field>
          <Field label={t("loadtest.deviceOSType")}>
            <AppSelect value={f.deviceOSType} onChange={(e) => set("deviceOSType", e.target.value)}>
              <option value="Android">Android</option>
              <option value="iOS">iOS</option>
              <option value="Huawei">Huawei</option>
            </AppSelect>
          </Field>
          <Field label={t("loadtest.reason")}>
            <input className={inputCls} value={f.reason} onChange={(e) => set("reason", e.target.value)} />
          </Field>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-primary">
            <input type="checkbox" checked={f.sendSms} onChange={(e) => set("sendSms", e.target.checked)} />
            {t("loadtest.sendSms")}
          </label>
          <label className="flex items-center gap-2 text-sm text-primary">
            <input type="checkbox" checked={f.setPhone} onChange={(e) => set("setPhone", e.target.checked)} />
            {t("loadtest.setPhone")}
          </label>
        </div>
      </Section>

      {/* API key */}
      <Section title={t("loadtest.section.apiKey")}>
        <label className="flex items-center gap-2 text-sm text-primary">
          <input type="checkbox" checked={f.embedApiKey} onChange={(e) => set("embedApiKey", e.target.checked)} />
          {t("loadtest.embedApiKey")}
        </label>
        {f.embedApiKey && (
          <Field label={t("loadtest.apiKey")}>
            <input className={inputCls} value={f.apiKey} onChange={(e) => set("apiKey", e.target.value)} />
          </Field>
        )}
      </Section>

      {msg && (
        <div className={`rounded-lg px-3 py-2 text-sm ring-1 ${msg.ok ? "bg-secondary ring-secondary text-success-primary" : "bg-secondary ring-error text-error-primary"}`}>
          {msg.text}
        </div>
      )}

      <div>
        <Button size="lg" iconLeading={Download01} isDisabled={busy} onPress={() => void generate()}>
          {busy ? t("testing.running") : t("loadtest.generate")}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-primary">{title}</h2>
      {children}
    </div>
  );
}
